import { useEffect, useMemo, useState } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import { getBranchEntries, type DashboardPharmacy } from "@/pages/MultiBranchDashboard/dashboardMetrics";
import {
  getFp34Summary,
  getFp34AdditionalFees,
  getPharmacyPrivateServicesIncome,
  getPharmacyPrivateServicesIncomeByDateRange,
  getDispensingIncome,
} from "@/dispatchers/titanTabDispatchers";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PoundSterling, ChevronRight, ArrowLeft } from "lucide-react";
import { useAiFp34Analyser } from "./AiFp34Analyser";
import { useToast } from "@/hooks/use-toast";

interface FinanceTabProps {
  isDarkMode: boolean;
  cardBgClass: string;
  textClass: string;
  mutedTextClass: string;
}

interface PLFigures {
  nhsDispensingIncome: number;
  nhsServicesIncome: number;
  privateServicesIncome: number;
  otherIncome: number;
  costOfDrugs: number;
  staffCosts: number;
  premisesCosts: number;
  otherOverheads: number;
}

interface StoreDef {
  id: string;
  name: string;
  odsCode: string;
}

// Every P&L line without an API behind it reports zero rather than an estimate.
const ZERO_FIGURES: PLFigures = {
  nhsDispensingIncome: 0,
  nhsServicesIncome: 0,
  privateServicesIncome: 0,
  otherIncome: 0,
  costOfDrugs: 0,
  staffCosts: 0,
  premisesCosts: 0,
  otherOverheads: 0,
};

function computeTotals(f: PLFigures) {
  const totalServicesIncome = f.nhsServicesIncome + f.privateServicesIncome;
  const totalIncome = f.nhsDispensingIncome + totalServicesIncome + f.otherIncome;
  const grossProfit = f.nhsDispensingIncome - f.costOfDrugs;
  const totalOverheads = f.staffCosts + f.premisesCosts + f.otherOverheads;
  const netProfit = totalIncome - f.costOfDrugs - totalOverheads;
  const netProfitMargin = totalIncome === 0 ? 0 : (netProfit / totalIncome) * 100;
  return { totalServicesIncome, totalIncome, grossProfit, totalOverheads, netProfit, netProfitMargin };
}

type MonthKey = string;

interface MonthDef {
  key: string;
  label: string;
  cycle: number;
  iso: string;
}

const MONTH_COUNT = 6;

const MONTHS: MonthDef[] = (() => {
  const now = new Date();
  return Array.from({ length: MONTH_COUNT }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (MONTH_COUNT - 1 - i), 1);
    const monthNum = d.getMonth() + 1;
    const yyyymm = `${d.getFullYear()}-${String(monthNum).padStart(2, "0")}`;
    return {
      key: yyyymm,
      label: d.toLocaleString("en-GB", { month: "short", year: "numeric" }),
      cycle: monthNum,
      iso: `${yyyymm}-01`,
    };
  });
})();

const MONTH_BY_KEY: Record<string, MonthDef> = Object.fromEntries(MONTHS.map((m) => [m.key, m]));
const MONTH_ORDER: string[] = MONTHS.map((m) => m.key);
const CURRENT_MONTH: string = MONTHS[MONTHS.length - 1]!.key;
const monthLabel = (k: string) => MONTH_BY_KEY[k]?.label ?? k;

const RANGE_START_ISO = `${MONTH_ORDER[0]}-01T00:00:00Z`;
const RANGE_END_ISO = (() => {
  const [y, m] = CURRENT_MONTH.split("-").map(Number);
  const end = new Date(Date.UTC(y!, m!, 0));
  return `${end.toISOString().slice(0, 10)}T23:59:59Z`;
})();

const monthKeyFromApi = (value?: string) =>
  typeof value === "string" && value.length >= 7 ? value.slice(0, 7) : "";

interface MonthlyIncomeRow { month?: string; monthLabel?: string; totalIncome?: number; }
interface PrivateServicesIncomeRange { pharmacyId?: string; monthlyIncome?: MonthlyIncomeRow[]; }

interface DispensingIncomeRow { cycleMonth?: string; breakdown?: any; }

interface ServiceItem { label: string; }

const NHS_SERVICE_ITEMS: ServiceItem[] = [
  { label: "Pharmacy First" },
  { label: "New Medicine Service (NMS)" },
  { label: "Hypertension Case-Finding" },
  { label: "Pharmacy Contraception Service" },
  { label: "NHS Flu Vaccination" },
];

const PRIVATE_SERVICE_ITEMS: ServiceItem[] = [
  { label: "Travel Clinic" },
  { label: "Weight Management" },
  { label: "Private Flu Vaccination" },
  { label: "Ear Wax Removal" },
];

type DrillKind = "fp34" | "services";
type ServiceKind = "nhs" | "private";

interface Fp34FeeItem {
  feeCode?: string | null;
  itemName?: string | null;
  packSize?: string | null;
  quantity?: number;
  endorsementTypeId?: number;
  endorsementType?: string | null;
  reimbursement?: number;
}

type FeeKey = "2A" | "2B-fitted" | "2B-delivery" | "2C" | "2E" | "2F" | "2G";

enum PrescriptionItemEndorseType {
  Unknown = 0,
  BrokenBulk = 1,
  MeasuredAndFitted = 2,
  OutOfPocketExpense = 3,
  ExtemporaneouslyDispense = 4,
  RebateClaimed = 5,
  SpFee = 6,
  NoCheaperStockObtainable = 8,
  PackagedDoses = 14,
  SeriousShortageProtocol = 20,
  SpecialsAndImportedUnlicensedMedicines = 25,
  WasteReductionScheme = 26,
}

interface PLLineDef {
  key: string;
  label: string;
  hint?: string;
  bold?: boolean;
  negative?: boolean;
  drill?: DrillKind;
  serviceKind?: ServiceKind;
  items?: ServiceItem[];
  value: (figs: PLFigures, totals: ReturnType<typeof computeTotals>) => number;
}

const PL_LINES: PLLineDef[] = [
  { key: "nhsDispensing",    label: "NHS Dispensing Income",    drill: "fp34",     value: (f) => f.nhsDispensingIncome },
  { key: "nhsServices",      label: "NHS Services Income",      drill: "services", serviceKind: "nhs",     items: NHS_SERVICE_ITEMS,     hint: "Pharmacy First, NMS, Hypertension, Contraception, NHS flu…", value: (f) => f.nhsServicesIncome },
  { key: "privateServices",  label: "Private Services Income",  drill: "services", serviceKind: "private", items: PRIVATE_SERVICE_ITEMS, hint: "Travel clinic, weight management, private flu, ear wax…",    value: (f) => f.privateServicesIncome },
  { key: "otherIncome",      label: "Other Income",                                value: (f) => f.otherIncome },
  { key: "totalIncome",      label: "Total Income",             bold: true,        value: (_f, t) => t.totalIncome },
  { key: "costOfDrugs",      label: "Cost of Drugs",            negative: true,    value: (f) => -f.costOfDrugs },
  { key: "grossProfit",      label: "Gross Profit (Dispensing)",bold: true,        value: (_f, t) => t.grossProfit },
];

// ── FP34 mapping ─────────────────────────────────────────────────────────────

interface Fp34View {
  totalSDR: number; discount: number; totalZDnonNCSO: number; totalZDNCSO: number; subTotalBasic: number;
  outOfPocket: number; consumables: number; containers: number; drugApplianceTotal: number;
  activityItems: number; activityRate: number; activityPayment: number;
  fee2A: number; fee2B: number; applianceHomeDelivery: number; cd2E: number; expensive2F: number;
  ssp2G: number; manuallyPriced: number; prescriptionSubTotal: number; totalPayment: number;
  methadone2C: number | null;
}

// Every Titan Core backed call routes its failure here; the dispatchers swallow
// the error and hand back null, so a null result is the failure signal. Prefer
// whatever the API said went wrong over the generic copy.
function apiFailure() {
  const capture = { message: "" };
  return {
    onError: (message: string) => { capture.message = message; },
    reason: (fallback: string) => capture.message || fallback,
  };
}

const asNum = (v: any) => (typeof v === "number" ? v : parseFloat(v)) || 0;
const round2 = (n: number) => Math.round(n * 100) / 100;

function mapFp34(m: any): Fp34View {
  const totalSDR = asNum(m.totalSDR);
  const totalZDnonNCSO = asNum(m.totalZD_NonNCSO);
  const totalZDNCSO = asNum(m.totalZD_NCSO);
  const subTotalBasic = asNum(m.subTotalBasic);
  const discount = round2(subTotalBasic - totalSDR - totalZDnonNCSO);
  const outOfPocket = asNum(m.totalOOPFee);
  const consumables = asNum(m.totalConsumableFee);
  const containers = asNum(m.totalContainerFee);
  const drugApplianceTotal = asNum(m.totalDrugsAndAppliances);
  const activityItems = asNum(m.activityClaimCount);
  const prescriptionSubTotal = asNum(m.manuallySub_TotalPrescriptionFees);
  const totalPayment = asNum(m.totalPayment);
  const activityPayment = round2(totalPayment - drugApplianceTotal - prescriptionSubTotal);
  const activityRate = activityItems > 0 ? round2(activityPayment / activityItems) : 0;
  return {
    totalSDR, discount, totalZDnonNCSO, totalZDNCSO, subTotalBasic,
    outOfPocket, consumables, containers, drugApplianceTotal,
    activityItems, activityRate, activityPayment,
    fee2A: asNum(m.totalItemFee),
    fee2B: asNum(m.totalMeasuredFittedFee),
    applianceHomeDelivery: 0,
    cd2E: asNum(m.totalCDFee),
    expensive2F: asNum(m.totalExpFee),
    ssp2G: asNum(m.totalSPFee),
    manuallyPriced: asNum(m.totalExpensiveFee),
    prescriptionSubTotal, totalPayment,
    methadone2C: asNum(m.totalMethadoneFee),
  };
}

const FEE_CATALOGS: Record<FeeKey, { title: string; feeCodes: string[]; endorsementTypeIds: PrescriptionItemEndorseType[] }> = {
  "2A": {
    title: "Unlicensed Medicines",
    feeCodes: [],
    endorsementTypeIds: [
      PrescriptionItemEndorseType.SpFee,
      PrescriptionItemEndorseType.SpecialsAndImportedUnlicensedMedicines,
      PrescriptionItemEndorseType.ExtemporaneouslyDispense,
    ],
  },
  "2B-fitted":   { title: "Appliances – Measured and Fitted", feeCodes: [],     endorsementTypeIds: [PrescriptionItemEndorseType.MeasuredAndFitted] },
  "2B-delivery": { title: "Appliances – Home Delivery",       feeCodes: ["2B"], endorsementTypeIds: [] },
  "2C":          { title: "Methadone Fee",                    feeCodes: [],     endorsementTypeIds: [PrescriptionItemEndorseType.PackagedDoses] },
  "2E":          { title: "Controlled Drug Schedules 2 & 3",  feeCodes: ["2E"], endorsementTypeIds: [] },
  "2F":          { title: "Expensive Prescription Fees",      feeCodes: ["2F"], endorsementTypeIds: [] },
  "2G":          { title: "Serious Shortage Protocol",        feeCodes: [],     endorsementTypeIds: [PrescriptionItemEndorseType.SeriousShortageProtocol] },
};

const FEE_KEYS = Object.keys(FEE_CATALOGS) as FeeKey[];

function feeKeyForItem(item: Fp34FeeItem): FeeKey | null {
  const endorsementTypeId = Number(item.endorsementTypeId);
  const byEndorsement = FEE_KEYS.find((key) => FEE_CATALOGS[key].endorsementTypeIds.includes(endorsementTypeId));
  if (byEndorsement) return byEndorsement;
  const feeCode = String(item.feeCode ?? "").trim().toUpperCase();
  if (!feeCode) return null;
  return FEE_KEYS.find((key) => FEE_CATALOGS[key].feeCodes.includes(feeCode)) ?? null;
}

function feeItemsFor(items: Fp34FeeItem[], feeKey: FeeKey) {
  return items.filter((item) => feeKeyForItem(item) === feeKey);
}

// ── Formatting ────────────────────────────────────────────────────────────────

// The fee items API leaves PackSize null for fees that aren't pack based, and
// can send it through as the text "null"; neither belongs on screen.
const packSizeText = (value?: string | null) => {
  const text = String(value ?? "").trim();
  return !text || text.toLowerCase() === "null" ? "" : text;
};

const noNegZero = (v: number) => (v === 0 ? 0 : v);
const fmt = (v: number) => `£${noNegZero(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const fmt2 = (v: number) => `${v < 0 ? "-" : ""}£${Math.abs(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCell = (v: number) => v < 0 ? `(${fmt(Math.abs(v))})` : fmt(v);

// ── Drill state ───────────────────────────────────────────────────────────────

interface PrivateServiceRow {
  serviceId: string;
  serviceName: string;
  consultations: number;
  income: number;
  share: number;
}

interface PrivateServicesIncome {
  pharmacyName: string;
  ods: string;
  monthLabel: string;
  noOfServices: number;
  topService: string;
  topServiceIncome: number;
  avgPerService: number;
  totalIncome: number;
  serviceIncomeBreakdown: PrivateServiceRow[];
}

interface DrillState {
  title: string;
  kind: DrillKind;
  serviceKind?: ServiceKind;
  value: number;
  pharmacyId: string;
  storeName: string;
  odsCode: string;
  monthLabel: string;
  cycleMonth: number;
  cycleMonthDate: string;
  items?: ServiceItem[];
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FinanceTab({ isDarkMode, cardBgClass, textClass, mutedTextClass }: FinanceTabProps) {
  const { titanTabState, appState } = useTitanApp() as any;
  const companyId: string = appState?.companyId ?? "";
  const dashboardStats: DashboardPharmacy[] = Array.isArray(titanTabState?.dashboardStats) ? titanTabState.dashboardStats : [];

  const STORES: StoreDef[] = useMemo(() => {
    const seen = new Set<string>();
    const branches = getBranchEntries(dashboardStats).filter((p) => {
      if (!p.pharmacyId || seen.has(p.pharmacyId)) return false;
      seen.add(p.pharmacyId);
      return true;
    });
    return branches.map((p) => ({
      id: p.pharmacyId,
      name: p.name,
      odsCode: p.ods || "—",
    }));
  }, [dashboardStats]);

  const currentStore = STORES[0];

  const [pivot, setPivot] = useState<"month" | "store">("month");
  const [pivotMonth, setPivotMonth] = useState<MonthKey>(CURRENT_MONTH);
  const [pivotStore, setPivotStore] = useState<string>("");
  const [drill, setDrill] = useState<DrillState | null>(null);

  useEffect(() => {
    if (STORES.length > 0 && !STORES.some((s) => s.id === pivotStore)) setPivotStore(STORES[0]!.id);
  }, [STORES, pivotStore]);

  const { toast } = useToast();
  const warn = (title: string, message: string) => toast({ variant: "destructive", title, description: message });

  const [privateIncome, setPrivateIncome] = useState<Record<string, Record<string, number>>>({});
  const [privateIncomeLoading, setPrivateIncomeLoading] = useState(false);

  const hasLiveStores = dashboardStats.length > 0;
  const storeIdsKey = useMemo(() => STORES.map((s) => s.id).join(","), [STORES]);

  useEffect(() => {
    if (!companyId || !hasLiveStores) {
      setPrivateIncome({});
      setPrivateIncomeLoading(false);
      return;
    }
    let cancelled = false;
    setPrivateIncomeLoading(true);
    Promise.all(
      STORES.map((store) =>
        getPharmacyPrivateServicesIncomeByDateRange(
          companyId,
          store.id,
          RANGE_START_ISO,
          RANGE_END_ISO,
        ).then((data: PrivateServicesIncomeRange | null) => ({ id: store.id, data })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const next: Record<string, Record<string, number>> = {};
      results.forEach(({ id, data }) => {
        const byMonth: Record<string, number> = {};
        (data?.monthlyIncome ?? []).forEach((row) => {
          const key = monthKeyFromApi(row?.month);
          if (key) byMonth[key] = Number(row?.totalIncome ?? 0);
        });
        next[id] = byMonth;
      });
      setPrivateIncome(next);
      setPrivateIncomeLoading(false);
    });
    return () => { cancelled = true; };
  }, [companyId, hasLiveStores, storeIdsKey]);

  const [dispensingIncome, setDispensingIncome] = useState<Record<string, Record<string, number>>>({});
  const [dispensingIncomeLoading, setDispensingIncomeLoading] = useState(false);

  useEffect(() => {
    if (!hasLiveStores) {
      setDispensingIncome({});
      setDispensingIncomeLoading(false);
      return;
    }
    let cancelled = false;
    setDispensingIncomeLoading(true);
    const failure = apiFailure();
    Promise.all(
      STORES.map((store) =>
        getDispensingIncome(store.id, RANGE_START_ISO, RANGE_END_ISO, failure.onError).then(
          (rows: DispensingIncomeRow[] | null) => ({ id: store.id, rows }),
        ),
      ),
    ).then((results) => {
      if (cancelled) return;
      if (results.some(({ rows }) => rows === null)) {
        warn("Couldn't load dispensing income", failure.reason("The NHS dispensing income couldn't be loaded. Please try again."));
      }
      const next: Record<string, Record<string, number>> = {};
      results.forEach(({ id, rows }) => {
        const byMonth: Record<string, number> = {};
        (rows ?? []).forEach((row) => {
          const key = monthKeyFromApi(row?.cycleMonth);
          if (key) byMonth[key] = asNum(row?.breakdown?.totalPayment);
        });
        next[id] = byMonth;
      });
      setDispensingIncome(next);
      setDispensingIncomeLoading(false);
    });
    return () => { cancelled = true; };
  }, [hasLiveStores, storeIdsKey]);

  const applyPrivateIncome = useMemo(
    () => (figs: PLFigures, storeId: string, monthKey: string): PLFigures => {
      const byMonth = privateIncome[storeId];
      const dispensingByMonth = dispensingIncome[storeId];
      const next = { ...figs };
      if (byMonth) next.privateServicesIncome = byMonth[monthKey] ?? 0;
      if (dispensingByMonth) next.nhsDispensingIncome = dispensingByMonth[monthKey] ?? 0;
      return next;
    },
    [privateIncome, dispensingIncome],
  );

  const ai = useAiFp34Analyser(isDarkMode);

  const borderClass = isDarkMode ? "border-gray-700" : "border-gray-200";
  const rowDivider  = isDarkMode ? "divide-gray-700" : "divide-gray-200";
  const subtleBg    = isDarkMode ? "bg-gray-900/40"  : "bg-gray-50";
  const toggleBg    = isDarkMode ? "bg-gray-800"     : "bg-gray-100";

  const activeStoreName = STORES.find((s) => s.id === pivotStore)?.name ?? currentStore?.name ?? "—";

  const columns = useMemo(() => {
    if (pivot === "month") {
      return STORES.map((store) => {
        const figs = applyPrivateIncome({ ...ZERO_FIGURES }, store.id, pivotMonth);
        return { key: store.id, label: store.name, figs, totals: computeTotals(figs) };
      });
    }
    const store = STORES.find((s) => s.id === pivotStore) ?? STORES[0];
    if (!store) return [];
    return MONTH_ORDER.map((m) => {
      const figs = applyPrivateIncome({ ...ZERO_FIGURES }, store.id, m);
      return { key: m, label: monthLabel(m), figs, totals: computeTotals(figs) };
    });
  }, [pivot, pivotMonth, pivotStore, STORES, applyPrivateIncome]);

  type Col = typeof columns[number];

  const openDrill = (line: PLLineDef, col: Col) => {
    if (!line.drill) return;
    const store = pivot === "month"
      ? (STORES.find((s) => s.id === col.key) ?? currentStore)
      : (STORES.find((s) => s.id === pivotStore) ?? currentStore);
    if (!store) return;
    const monthKey = pivot === "month" ? pivotMonth : (col.key as MonthKey);
    setDrill({
      title: line.label,
      kind: line.drill,
      serviceKind: line.serviceKind,
      value: line.value(col.figs, col.totals),
      pharmacyId: store.id,
      storeName: store.name,
      odsCode: store.odsCode,
      monthLabel: monthLabel(monthKey),
      cycleMonth: MONTH_BY_KEY[monthKey]?.cycle ?? 0,
      cycleMonthDate: MONTH_BY_KEY[monthKey]?.iso ?? "",
      items: line.items,
    });
  };

  // ── Drill view (replaces tab content, same as Members/Services pattern) ─────

  if (drill) {
    return (
      <DrillView
        drill={drill}
        companyId={companyId}
        isDarkMode={isDarkMode}
        cardBgClass={cardBgClass}
        textClass={textClass}
        mutedTextClass={mutedTextClass}
        onBack={() => setDrill(null)}
      />
    );
  }

  // ── AI FP34 Analyser results (full-replacement view) ────────────────────────

  if (ai.hasImportedData) {
    return (
      <div className={`flex-1 flex flex-col overflow-hidden ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
        <div className={`px-6 py-4 border-b ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`}>
          <Button
            variant="ghost"
            onClick={ai.clear}
            className={`${isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""}`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Finance
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">{ai.results}</div>
      </div>
    );
  }

  // ── P&L table view ────────────────────────────────────────────────────────

  return (
    <div className="p-6">
      {ai.overlay}
      <div className="space-y-6">
      {ai.errorBanner}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className={`text-xl font-bold ${textClass} flex items-center gap-2`}>
            <PoundSterling className="w-5 h-5" />
            Profit &amp; Loss
          </h2>
          <p className={`text-sm mt-1 ${mutedTextClass}`}>
            {pivot === "month"
              ? `All stores compared for ${monthLabel(pivotMonth)}.`
              : `Month-by-month trend for ${activeStoreName}.`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`flex gap-1 p-1 rounded-lg ${toggleBg}`}>
            {(["month", "store"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setPivot(mode)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                  pivot === mode
                    ? isDarkMode ? "bg-gray-700 text-white shadow-sm" : "bg-white text-gray-900 shadow-sm"
                    : isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {mode === "month" ? "By Month" : "By Store"}
              </button>
            ))}
          </div>
          {pivot === "month" ? (
            <Select value={pivotMonth} onValueChange={(v) => setPivotMonth(v as MonthKey)}>
              <SelectTrigger className="w-[170px] h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_ORDER.map((m) => (
                  <SelectItem key={m} value={m} className="text-xs">
                    {monthLabel(m)}{m === CURRENT_MONTH ? " (current)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Select value={pivotStore} onValueChange={(v) => setPivotStore(v)}>
              <SelectTrigger className="w-[190px] h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STORES.map((s) => (
                  <SelectItem key={s.id} value={s.id} className="text-xs">
                    {s.name}{s.id === currentStore?.id ? " (current)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <Card className={`${cardBgClass} border ${borderClass}`}>
        <CardHeader>
          <CardTitle className={`text-sm font-semibold ${textClass}`}>Profit &amp; Loss Statement</CardTitle>
          <CardDescription className={`text-xs ${mutedTextClass}`}>
            {pivot === "month"
              ? `All stores · ${monthLabel(pivotMonth)}`
              : `${activeStoreName} · ${monthLabel(MONTH_ORDER[0]!)} – ${monthLabel(MONTH_ORDER[MONTH_ORDER.length - 1]!)}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {privateIncomeLoading || dispensingIncomeLoading ? (
            <div className="space-y-3">
              <div className="flex gap-3">
                <Skeleton className="h-4 w-40 animate-pulse" />
                {columns.map((c) => (
                  <Skeleton key={c.key} className="h-4 flex-1 animate-pulse" />
                ))}
              </div>
              {PL_LINES.map((line) => (
                <div key={line.key} className="flex gap-3">
                  <Skeleton className="h-8 w-40 animate-pulse" />
                  {columns.map((c) => (
                    <Skeleton key={c.key} className="h-8 flex-1 animate-pulse" />
                  ))}
                </div>
              ))}
            </div>
          ) : columns.length === 0 ? (
            <p className={`text-sm py-6 text-center ${mutedTextClass}`}>No pharmacies available for this company.</p>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className={`text-xs uppercase tracking-wide ${mutedTextClass} border-b ${borderClass}`}>
                  <th className="text-left py-2 px-3 font-medium whitespace-nowrap">
                    {pivot === "month" ? "Store" : "Month"}
                  </th>
                  {columns.map((c) => {
                    const isCurrent = pivot === "store" && c.key === CURRENT_MONTH;
                    return (
                      <th key={c.key} className={`text-right py-2 px-3 font-medium whitespace-nowrap ${isCurrent ? textClass : ""}`}>
                        {c.label}{isCurrent ? " (current)" : ""}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className={`divide-y ${rowDivider}`}>
                {PL_LINES.map((line) => (
                  <tr key={line.key} className={line.bold ? subtleBg : ""}>
                    <td className="py-2 px-3 align-top">
                      <div className={`${line.bold ? "font-semibold" : ""} ${textClass}`}>{line.label}</div>
                      {line.hint && <div className={`text-[11px] ${mutedTextClass}`}>{line.hint}</div>}
                    </td>
                    {columns.map((c) => {
                      const v = line.value(c.figs, c.totals);
                      const isCurrent = pivot === "store" && c.key === CURRENT_MONTH;
                      return (
                        <td
                          key={c.key}
                          onClick={line.drill ? () => openDrill(line, c) : undefined}
                          className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${isCurrent && !line.bold ? subtleBg : ""} ${line.bold ? "font-semibold" : ""} ${line.negative ? "text-red-500" : textClass} ${line.drill ? "cursor-pointer hover:underline hover:bg-blue-500/5" : ""}`}
                        >
                          {fmtCell(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

// ── DrillView ─────────────────────────────────────────────────────────────────

function DrillView({
  drill, companyId, isDarkMode, cardBgClass, textClass, mutedTextClass, onBack,
}: {
  drill: DrillState;
  companyId: string;
  isDarkMode: boolean;
  cardBgClass: string;
  textClass: string;
  mutedTextClass: string;
  onBack: () => void;
}) {
  const isFp34 = drill.kind === "fp34";
  const [fp34, setFp34] = useState<Fp34View | null>(null);
  const [fp34Loading, setFp34Loading] = useState(isFp34);
  const isPrivate = drill.kind === "services" && drill.serviceKind === "private";
  const [privateData, setPrivateData] = useState<PrivateServicesIncome | null>(null);
  const [privateLoading, setPrivateLoading] = useState(isPrivate);
  const borderClass = isDarkMode ? "border-gray-700" : "border-gray-200";
  const { toast } = useToast();
  const warn = (title: string, message: string) => toast({ variant: "destructive", title, description: message });
  const [activeFeeKey, setActiveFeeKey] = useState<FeeKey | null>(null);
  const activeFee = activeFeeKey ? FEE_CATALOGS[activeFeeKey] : null;
  const [additionalFees, setAdditionalFees] = useState<Fp34FeeItem[]>([]);
  const [additionalFeesLoading, setAdditionalFeesLoading] = useState(isFp34);
  const activeFeeItems = useMemo(
    () => (activeFeeKey ? feeItemsFor(additionalFees, activeFeeKey) : []),
    [additionalFees, activeFeeKey],
  );

  useEffect(() => {
    if (!isFp34) { setFp34(null); setFp34Loading(false); return; }
    let cancelled = false;
    setFp34Loading(true);
    const failure = apiFailure();
    getFp34Summary(drill.pharmacyId, drill.cycleMonthDate, failure.onError).then((model) => {
      if (cancelled) return;
      if (!model) {
        warn("Couldn't load the FP34 summary", failure.reason("The FP34 summary couldn't be loaded. Please try again."));
      }
      setFp34(model ? mapFp34(model) : null);
      setFp34Loading(false);
    });
    return () => { cancelled = true; };
  }, [isFp34, drill.pharmacyId, drill.cycleMonthDate]);

  useEffect(() => {
    if (!isFp34) { setAdditionalFees([]); setAdditionalFeesLoading(false); return; }
    let cancelled = false;
    setAdditionalFeesLoading(true);
    const failure = apiFailure();
    getFp34AdditionalFees(drill.pharmacyId, drill.cycleMonthDate, failure.onError).then((items: Fp34FeeItem[] | null) => {
      if (cancelled) return;
      if (items === null) {
        warn("Couldn't load additional fees", failure.reason("The FP34 additional fee items couldn't be loaded. Please try again."));
      }
      setAdditionalFees(items ?? []);
      setAdditionalFeesLoading(false);
    });
    return () => { cancelled = true; };
  }, [isFp34, drill.pharmacyId, drill.cycleMonthDate]);

  useEffect(() => {
    if (!isPrivate) { setPrivateData(null); return; }
    if (!companyId || !drill.pharmacyId) { setPrivateData(null); setPrivateLoading(false); return; }
    let cancelled = false;
    setPrivateLoading(true);
    const month = `${drill.cycleMonthDate}T00:00:00Z`;
    getPharmacyPrivateServicesIncome(companyId, drill.pharmacyId, month).then((data) => {
      if (cancelled) return;
      setPrivateData(data ?? null);
      setPrivateLoading(false);
    });
    return () => { cancelled = true; };
  }, [isPrivate, companyId, drill.pharmacyId, drill.cycleMonthDate]);

  const showFp34Skeleton = isFp34 && fp34Loading;
  const showFeeItemsSkeleton = !!activeFee && additionalFeesLoading;
  const showPrivateSkeleton = isPrivate && privateLoading;

  // Compute services breakdown for stats
  const svcItems = drill.items ?? [];

  // Stats use compact numeric / short values — same text-2xl treatment as Members tab
  const stats = isFp34
    ? fp34
      ? [
          { label: "Drug & Appliance",   value: fmt(fp34.drugApplianceTotal) },
          { label: "Activity Payment",   value: fmt(fp34.activityPayment) },
          { label: "Additional Fees",    value: fmt(fp34.fee2A + fp34.fee2B + fp34.cd2E + fp34.expensive2F) },
          { label: "Cycle Month",        value: String(drill.cycleMonth) },
          { label: "Total Payment",      value: fmt(fp34.totalPayment), highlight: true },
        ]
      : []
    : isPrivate && privateData
    ? [
        { label: "No. of Services",    value: String(privateData.noOfServices) },
        { label: "Top Service",        value: privateData.topService || "—" },
        { label: "Top Service Income", value: fmt2(privateData.topServiceIncome) },
        { label: "Avg per Service",    value: fmt2(privateData.avgPerService) },
        { label: "Total Income",       value: fmt2(privateData.totalIncome), highlight: true },
      ]
    : [
        { label: "No. of Services",    value: "0" },
        { label: "Top Service",        value: "—" },
        { label: "Top Service Income", value: fmt(0) },
        { label: "Avg per Service",    value: fmt(0) },
        { label: "Total Income",       value: fmt(drill.value), highlight: true },
      ];

  return (
    <div className={`flex-1 flex flex-col overflow-hidden ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      {/* Header — identical structure to Members/Services */}
      <div className={`px-6 py-4 border-b ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`}>
        <Button
          variant="ghost"
          onClick={activeFee ? () => setActiveFeeKey(null) : onBack}
          className={`mb-4 ${isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""}`}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {activeFee ? "Back to FP34 Statement" : "Back to Finance"}
        </Button>
        <h1 className={`text-2xl font-bold ${textClass}`}>
          {activeFee ? activeFee.title : isFp34 ? "FP34 Statement" : drill.title}
        </h1>
        <p className={`text-sm mt-1 ${mutedTextClass}`}>
          {drill.storeName} · {drill.monthLabel} · ODS {drill.odsCode}
        </p>
      </div>

      {/* Stats grid — identical structure to Members/Services */}
      <div className="px-6 pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {showFp34Skeleton || showPrivateSkeleton
            ? Array.from({ length: 5 }).map((_, i) => (
                <Card key={i} className={`${cardBgClass} border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                  <CardContent className="pt-6">
                    <Skeleton className="h-4 w-24 animate-pulse" />
                    <Skeleton className="h-7 w-20 mt-3 animate-pulse" />
                  </CardContent>
                </Card>
              ))
            : stats.map((s) => (
                <Card key={s.label} className={`${cardBgClass} border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                  <CardContent className="pt-6">
                    <div className={`text-sm font-medium ${mutedTextClass}`}>{s.label}</div>
                    <div className={`text-2xl font-bold mt-2 ${s.highlight ? "text-green-500" : textClass}`}>
                      {s.value}
                    </div>
                  </CardContent>
                </Card>
              ))}
        </div>
      </div>

      {/* Scrollable detail */}
      <div className="flex-1 overflow-y-auto p-6">
        <Card className={`${cardBgClass} border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
          <CardHeader>
            <CardTitle className={`text-sm font-semibold ${textClass}`}>
              {activeFee ? `Itemised ${activeFee.title}` : isFp34 ? "FP34 Breakdown" : "Service Income Breakdown"}
            </CardTitle>
            <CardDescription className={mutedTextClass}>
              {activeFee
                ? showFeeItemsSkeleton ? "Loading additional fee items…" : `Item, pack size, quantity and reimbursement value for each item under ${activeFee.title.toLowerCase()}`
                : isFp34
                ? fp34Loading ? "Loading FP34 summary…" : fp34 ? "NHS dispensing payment breakdown for this cycle" : "FP34 summary is not available for this cycle"
                : showPrivateSkeleton ? "Loading service income…" : `Income breakdown for ${drill.title.toLowerCase()}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showFeeItemsSkeleton ? (
              <FeeItemsDetailSkeleton borderClass={borderClass} />
            ) : activeFee ? (
              <FeeItemsDetail items={activeFeeItems} isDarkMode={isDarkMode} textClass={textClass} mutedTextClass={mutedTextClass} borderClass={borderClass} />
            ) : showFp34Skeleton ? (
              <Fp34DetailSkeleton borderClass={borderClass} />
            ) : showPrivateSkeleton ? (
              <ServicesDetailSkeleton borderClass={borderClass} />
            ) : isFp34 ? (
              fp34 ? (
                <Fp34Detail fp34={fp34} isDarkMode={isDarkMode} textClass={textClass} mutedTextClass={mutedTextClass} borderClass={borderClass} onOpenFeeItems={setActiveFeeKey} />
              ) : (
                <p className={`text-sm py-6 text-center ${mutedTextClass}`}>
                  Unable to load the FP34 summary for this cycle.
                </p>
              )
            ) : isPrivate && privateData ? (
              <PrivateServicesDetail data={privateData} isDarkMode={isDarkMode} textClass={textClass} mutedTextClass={mutedTextClass} borderClass={borderClass} />
            ) : (
              <ServicesDetail items={drill.items ?? []} total={drill.value} isDarkMode={isDarkMode} textClass={textClass} mutedTextClass={mutedTextClass} borderClass={borderClass} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── FP34 detail ───────────────────────────────────────────────────────────────

function Fp34DetailSkeleton({ borderClass }: { borderClass: string }) {
  return (
    <div className="space-y-1">
      {Array.from({ length: 14 }).map((_, i) => (
        <div key={i} className={`flex items-center gap-4 py-2.5 border-b ${borderClass}`}>
          <Skeleton className="h-4 w-8 animate-pulse" />
          <Skeleton className="h-4 flex-1 max-w-[260px] animate-pulse" />
          <Skeleton className="h-6 w-28 ml-auto animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function Fp34Detail({ fp34, isDarkMode, textClass, mutedTextClass, borderClass, onOpenFeeItems }: {
  fp34: Fp34View; isDarkMode: boolean;
  textClass: string; mutedTextClass: string; borderClass: string;
  onOpenFeeItems: (feeKey: FeeKey) => void;
}) {
  const cellBg  = isDarkMode ? "bg-gray-800"  : "bg-white";
  const sectionBg = isDarkMode ? "bg-gray-700/60" : "bg-gray-100";
  const totalBg   = isDarkMode ? "bg-gray-600"    : "bg-gray-500";

  type Row =
    | { kind: "row";     ref?: string; label: string; value: number | null; blank?: boolean; feeKey?: FeeKey }
    | { kind: "section"; label: string; value: number }
    | { kind: "total";   label: string; value: number }
    | { kind: "heading"; label: string }
    | { kind: "activity" };

  const rows: Row[] = [
    { kind: "row",     label: "Total SDR",                                  value: fp34.totalSDR },
    { kind: "row",     label: "Discount",                                   value: fp34.discount },
    { kind: "row",     label: "Total ZD (non NCSO)",                        value: fp34.totalZDnonNCSO },
    { kind: "row",     label: "Total ZD (NCSO)",                            value: fp34.totalZDNCSO,         blank: fp34.totalZDNCSO === 0 },
    { kind: "section", label: "Sub Total Basic",                            value: fp34.subTotalBasic },
    { kind: "row",     label: "Out Of Pocket expenses",                     value: fp34.outOfPocket,         blank: fp34.outOfPocket === 0 },
    { kind: "row",     label: "Payment for consumables",                    value: fp34.consumables },
    { kind: "row",     label: "Payment for containers",                     value: fp34.containers,          blank: fp34.containers === 0 },
    { kind: "total",   label: "Total of Drug and Appliance Costs",          value: fp34.drugApplianceTotal },
    { kind: "activity" },
    { kind: "heading", label: "Additional fees" },
    { kind: "row",     ref: "2A", label: "Unlicensed medicines",            value: fp34.fee2A,                 feeKey: "2A" },
    { kind: "row",     ref: "2B", label: "Appliances – measured and fitted",value: fp34.fee2B,                 feeKey: "2B-fitted" },
    { kind: "row",     ref: "2B", label: "Appliances – home delivery",      value: fp34.applianceHomeDelivery, blank: fp34.applianceHomeDelivery === 0, feeKey: "2B-delivery" },
    { kind: "row",     ref: "2C", label: "Methadone Fee",                   value: fp34.methadone2C, blank: fp34.methadone2C === 0, feeKey: "2C" },
    { kind: "row",     ref: "2E", label: "Controlled drug schedules 2 & 3", value: fp34.cd2E,                  feeKey: "2E" },
    { kind: "row",     ref: "2F", label: "Expensive prescription fees",     value: fp34.expensive2F,           feeKey: "2F" },
    { kind: "row",     ref: "2G", label: "Serious Shortage Protocol",       value: fp34.ssp2G,                 blank: fp34.ssp2G === 0, feeKey: "2G" },
    { kind: "section", label: "Sub Total of Prescription",                  value: fp34.prescriptionSubTotal },
    { kind: "total",   label: "TOTAL PAYMENT",                              value: fp34.totalPayment },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <colgroup>
          <col className="w-12" />
          <col />
          <col className="w-40" />
        </colgroup>
        <thead>
          <tr className={`text-xs uppercase tracking-wide ${mutedTextClass} border-b ${borderClass}`}>
            <th className="text-left py-2 px-2 font-medium">Ref</th>
            <th className="text-left py-2 px-3 font-medium">Description</th>
            <th className="text-right py-2 px-3 font-medium">Amount (£)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            if (row.kind === "heading") {
              return (
                <tr key={i}>
                  <td colSpan={3} className={`pt-5 pb-1 px-3 text-xs font-semibold uppercase tracking-wide ${mutedTextClass}`}>
                    {row.label}
                  </td>
                </tr>
              );
            }
            if (row.kind === "activity") {
              return (
                <tr key={i} className={`border-b ${borderClass}`}>
                  <td className={`py-2 px-2 text-xs font-mono ${mutedTextClass}`} />
                  <td className={`py-2 px-3 ${textClass}`}>
                    Activity Payment
                    <span className={`ml-2 text-xs ${mutedTextClass}`}>
                      {fp34.activityItems.toLocaleString("en-GB")} items @ {fmt2(fp34.activityRate)}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={`inline-block min-w-[120px] text-right tabular-nums border ${borderClass} rounded px-2 py-0.5 ${cellBg} ${textClass}`}>
                      {fmt2(fp34.activityPayment)}
                    </span>
                  </td>
                </tr>
              );
            }
            if (row.kind === "section") {
              return (
                <tr key={i} className={`${sectionBg} border-y ${borderClass}`}>
                  <td className="py-2 px-2" />
                  <td className={`py-2 px-3 font-semibold ${textClass}`}>{row.label}</td>
                  <td className={`py-2 px-3 text-right font-semibold tabular-nums ${textClass}`}>
                    {fmt2(row.value)}
                  </td>
                </tr>
              );
            }
            if (row.kind === "total") {
              return (
                <tr key={i} className={`${totalBg} text-white`}>
                  <td className="py-3 px-2" />
                  <td className="py-3 px-3 font-bold text-sm">{row.label}</td>
                  <td className="py-3 px-3 text-right font-bold tabular-nums text-sm">
                    {fmt2(row.value)}
                  </td>
                </tr>
              );
            }
            // Regular row
            const display =
              row.value === null ? "NULL" : row.blank ? "" : fmt2(row.value);
            const isDrillable = !!row.feeKey && !row.blank && row.value !== null;
            const openThisFee = isDrillable ? () => onOpenFeeItems(row.feeKey!) : undefined;
            return (
              <tr key={i} className={`border-b ${borderClass}`}>
                <td className={`py-2 px-2 text-xs font-mono font-medium ${mutedTextClass}`}>
                  {row.ref ?? ""}
                </td>
                <td className={`py-2 px-3 ${textClass}`}>
                  {isDrillable ? (
                    <button
                      type="button"
                      onClick={openThisFee}
                      className="inline-flex items-center gap-1 cursor-pointer hover:underline"
                    >
                      {row.label}
                      <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                    </button>
                  ) : (
                    row.label
                  )}
                </td>
                <td
                  className={`py-2 px-3 text-right ${isDrillable ? "cursor-pointer hover:bg-blue-500/5" : ""}`}
                  onClick={openThisFee}
                >
                  <span className={`inline-block min-w-[120px] text-right tabular-nums border ${borderClass} rounded px-2 py-0.5 ${cellBg} ${row.value === null ? mutedTextClass : textClass}`}>
                    {display}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className={`text-xs mt-4 ${mutedTextClass}`}>
        Services income is excluded from this FP34 view and is reported separately.
      </p>
    </div>
  );
}

// ── Services detail ───────────────────────────────────────────────────────────

function ServicesDetailSkeleton({ borderClass }: { borderClass: string }) {
  return (
    <div className="space-y-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`flex items-center gap-4 py-3 border-b ${borderClass}`}>
          <Skeleton className="h-4 flex-1 max-w-[200px] animate-pulse" />
          <Skeleton className="h-4 w-16 animate-pulse" />
          <Skeleton className="h-1.5 flex-1 max-w-[160px] animate-pulse" />
          <Skeleton className="h-4 w-20 ml-auto animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function PrivateServicesDetail({ data, isDarkMode, textClass, mutedTextClass, borderClass }: {
  data: PrivateServicesIncome; isDarkMode: boolean;
  textClass: string; mutedTextClass: string; borderClass: string;
}) {
  const rows = data.serviceIncomeBreakdown ?? [];
  const totalBg = isDarkMode ? "bg-gray-600" : "bg-gray-500";

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className={`text-xs uppercase tracking-wide ${mutedTextClass} border-b ${borderClass}`}>
          <th className="text-left py-2 px-3 font-medium">Service</th>
          <th className="text-right py-2 px-3 font-medium w-28">Consultations</th>
          <th className="text-right py-2 px-3 font-medium w-20">Share</th>
          <th className="py-2 px-3 w-40" />
          <th className="text-right py-2 px-3 font-medium w-28">Income</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.serviceId} className={`border-b ${borderClass}`}>
            <td className={`py-3 px-3 ${textClass}`}>{r.serviceName}</td>
            <td className={`py-3 px-3 text-right tabular-nums ${mutedTextClass}`}>{r.consultations}</td>
            <td className={`py-3 px-3 text-right tabular-nums text-xs ${mutedTextClass}`}>{r.share.toFixed(1)}%</td>
            <td className="py-3 px-3">
              <div className={`w-full rounded-full h-1.5 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}>
                <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(0, r.share))}%` }} />
              </div>
            </td>
            <td className={`py-3 px-3 text-right tabular-nums font-medium ${textClass}`}>{fmt2(r.income)}</td>
          </tr>
        ))}
        <tr className={`${totalBg} text-white`}>
          <td className="py-3 px-3 font-bold text-sm" colSpan={4}>Total</td>
          <td className="py-3 px-3 text-right font-bold tabular-nums text-sm">{fmt2(data.totalIncome)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function ServicesDetail({ items, total, isDarkMode, textClass, mutedTextClass, borderClass }: {
  items: ServiceItem[]; total: number; isDarkMode: boolean;
  textClass: string; mutedTextClass: string; borderClass: string;
}) {
  const rows = items.map((i) => ({ label: i.label, amount: 0 }));
  const totalBg = isDarkMode ? "bg-gray-600" : "bg-gray-500";

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className={`text-xs uppercase tracking-wide ${mutedTextClass} border-b ${borderClass}`}>
          <th className="text-left py-2 px-3 font-medium">Service</th>
          <th className="text-right py-2 px-3 font-medium w-24">Share</th>
          <th className="py-2 px-3 w-40" />
          <th className="text-right py-2 px-3 font-medium w-28">Income</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const pct = total === 0 ? 0 : (r.amount / total) * 100;
          return (
            <tr key={r.label} className={`border-b ${borderClass}`}>
              <td className={`py-3 px-3 ${textClass}`}>{r.label}</td>
              <td className={`py-3 px-3 text-right tabular-nums text-xs ${mutedTextClass}`}>{pct.toFixed(1)}%</td>
              <td className="py-3 px-3">
                <div className={`w-full rounded-full h-1.5 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}>
                  <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </td>
              <td className={`py-3 px-3 text-right tabular-nums font-medium ${textClass}`}>{fmt(r.amount)}</td>
            </tr>
          );
        })}
        <tr className={`${totalBg} text-white`}>
          <td className="py-3 px-3 font-bold text-sm" colSpan={3}>Total</td>
          <td className="py-3 px-3 text-right font-bold tabular-nums text-sm">{fmt(total)}</td>
        </tr>
      </tbody>
    </table>
  );
}

// ── Expensive prescriptions itemised detail ────────────────────────────────────

function FeeItemsDetailSkeleton({ borderClass }: { borderClass: string }) {
  return (
    <div className="space-y-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className={`flex items-center gap-4 py-3 border-b ${borderClass}`}>
          <Skeleton className="h-4 flex-1 max-w-[320px] animate-pulse" />
          <Skeleton className="h-4 w-24 animate-pulse" />
          <Skeleton className="h-4 w-12 ml-auto animate-pulse" />
          <Skeleton className="h-4 w-24 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function FeeItemsDetail({ items, isDarkMode, textClass, mutedTextClass, borderClass }: {
  items: Fp34FeeItem[]; isDarkMode: boolean;
  textClass: string; mutedTextClass: string; borderClass: string;
}) {
  const total = round2(items.reduce((sum, item) => sum + asNum(item.reimbursement), 0));
  const totalBg = isDarkMode ? "bg-gray-600" : "bg-gray-500";

  if (items.length === 0) {
    return (
      <p className={`text-sm py-6 text-center ${mutedTextClass}`}>
        No items found for this fee in this cycle.
      </p>
    );
  }

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className={`text-xs uppercase tracking-wide ${mutedTextClass} border-b ${borderClass}`}>
          <th className="text-left py-2 px-3 font-medium">Item</th>
          <th className="text-left py-2 px-3 font-medium">Pack Size</th>
          <th className="text-right py-2 px-3 font-medium w-24">Quantity</th>
          <th className="text-right py-2 px-3 font-medium w-32">Reimbursement (£)</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, i) => (
          <tr key={`${item.itemName ?? ""}-${item.packSize ?? ""}-${i}`} className={`border-b ${borderClass}`}>
            <td className={`py-3 px-3 ${textClass}`}>
              {item.itemName || "—"}
              {item.endorsementType && (
                <span className={`ml-2 text-xs font-mono ${mutedTextClass}`}>{item.endorsementType}</span>
              )}
            </td>
            <td className={`py-3 px-3 ${mutedTextClass}`}>{packSizeText(item.packSize)}</td>
            <td className={`py-3 px-3 text-right tabular-nums ${textClass}`}>{asNum(item.quantity)}</td>
            <td className={`py-3 px-3 text-right tabular-nums font-medium ${textClass}`}>{fmt2(asNum(item.reimbursement))}</td>
          </tr>
        ))}
        <tr className={`${totalBg} text-white`}>
          <td className="py-3 px-3 font-bold text-sm" colSpan={3}>Total</td>
          <td className="py-3 px-3 text-right font-bold tabular-nums text-sm">{fmt2(total)}</td>
        </tr>
      </tbody>
    </table>
  );
}
